import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgressMui from "@mui/material/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import api from "../api";
import { AdminContainer } from "../components/layouts";
import SimpleTable from "../components/SimpleTable";
import displayPhone from "../modules/displayPhone";

const formatPhone = (phone) => displayPhone(phone?.replace(/^\+/, ""));

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};



const useStyles = makeStyles((theme) => ({
  section: { marginBottom: theme.spacing(4) },
  sectionTitle: { marginBottom: theme.spacing(2), fontWeight: 600 },
  filters: {
    display: "flex", gap: theme.spacing(2), marginBottom: theme.spacing(2),
    alignItems: "center", flexWrap: "wrap",
  },
  statusActive: { backgroundColor: theme.palette.success.main, color: "#fff" },
  statusPending: { backgroundColor: theme.palette.warning.main, color: "#fff" },
  statusFailed: { backgroundColor: theme.palette.error.main, color: "#fff" },
  statusReleased: { backgroundColor: theme.palette.grey[500], color: "#fff" },
  actions: { display: "flex", gap: theme.spacing(1), alignItems: "center" },
  partnerLink: {
    cursor: "pointer", color: theme.palette.primary.main, textDecoration: "underline",
    "&:hover": { color: theme.palette.primary.dark },
  },
  webhookUrl: { fontSize: "0.75rem", color: theme.palette.text.secondary, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
}));

function StatusChip({ status, classes }) {
  const labels = {
    active: "Active", pending_approval: "Pending Approval", pending: "Provisioning",
    failed: "Failed", released: "Released", suspended: "Suspended",
  };
  const classMap = {
    active: classes.statusActive, failed: classes.statusFailed, released: classes.statusReleased,
  };
  return <Chip size="small" label={labels[status] || status} className={classMap[status] || classes.statusPending} />;
}

// ─── Pending Requests ───────────────────────────────────────────

function PendingRequestsSection({ classes, onActionComplete }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const history = useHistory();

  const load = useCallback(async () => {
    try {
      const resp = await api.getPendingPhoneRequests();
      const items = api.pickData(resp) || [];

      // Resolve partner names
      const partnerIds = [...new Set(items.map((r) => r.partnerId))].filter(Boolean);
      if (partnerIds.length > 0) {
        const partnerResults = await Promise.all(
          partnerIds.map((id) => api.getPartner({ id }).then((r) => r?.data).catch(() => null))
        );
        const partners = {};
        partnerResults.forEach((p) => { if (p?.id) partners[p.id] = p.name; });
        items.forEach((r) => { r.partnerName = partners[r.partnerId] || null; });
      }

      setRequests(items);
    } catch { toast.error("Failed to load pending requests"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const i = setInterval(load, 10000); return () => clearInterval(i); }, [load]);

  const handleApprove = async (req) => {
    try {
      await api.approvePhoneNumberRequest({ id: req.partnerId, phoneId: req.id });
      toast.success("Request approved — number is now provisioning");
      // Update row status locally before reload so user sees the change
      setRequests((prev) => prev.map((r) => r.id === req.id ? { ...r, status: "pending" } : r));
      setTimeout(() => { load(); onActionComplete?.(); }, 2000);
    } catch (e) { toast.error(e?.response?.data?.error?.message || "Failed to approve"); }
  };

  const handleReject = async (req) => {
    try {
      await api.rejectPhoneNumberRequest({ id: req.partnerId, phoneId: req.id });
      toast.success("Request rejected");
      setRequests((prev) => prev.filter((r) => r.id !== req.id));
      onActionComplete?.();
    } catch (e) { toast.error(e?.response?.data?.error?.message || "Failed to reject"); }
  };

  const handleRetry = async (req) => {
    try {
      await api.retryPhoneNumberProvision({ id: req.partnerId, phoneId: req.id });
      toast.success("Retrying provisioning...");
      setRequests((prev) => prev.map((r) => r.id === req.id ? { ...r, status: "pending", provisionError: null } : r));
      setTimeout(() => { load(); onActionComplete?.(); }, 2000);
    } catch (e) { toast.error(e?.response?.data?.error?.message || "Failed to retry"); }
  };

  if (loading) return <CircularProgress size={24} />;
  if (requests.length === 0) return <Typography variant="body2" color="textSecondary">No pending requests.</Typography>;

  /* eslint-disable react/jsx-key */
  return (
    <SimpleTable
      rows={requests}
      headers={["Partner", "Requested By", "Requested At", "Status", "Actions"]}
      toCells={(req) => [
        <span className={classes.partnerLink} onClick={() => history.push(`/partners/${req.partnerId}`)}>
          {req.partnerName || `Partner #${req.partnerId}`}
        </span>,
        req.requestedByUserName || `User #${req.requestedByUserId || "?"}`,
        formatDate(req.requestedAt),
        <Box>
          <StatusChip status={req.status} classes={classes} />
          {req.provisionError && (
            <Typography variant="caption" display="block" style={{ color: "#d32f2f", marginTop: 4, maxWidth: 300 }}>
              {req.provisionError}
            </Typography>
          )}
        </Box>,
        <div className={classes.actions}>
          {req.status === "pending_approval" && (
            <>
              <Button variant="contained" color="primary" size="small" startIcon={<CheckCircleIcon />} onClick={() => handleApprove(req)}>Approve</Button>
              <Button variant="outlined" size="small" startIcon={<CancelIcon />} onClick={() => handleReject(req)}>Reject</Button>
            </>
          )}
          {req.status === "failed" && (
            <>
              <Button variant="contained" color="primary" size="small" startIcon={<RefreshIcon />} onClick={() => handleRetry(req)}>Retry</Button>
              <Button variant="outlined" size="small" startIcon={<CancelIcon />} onClick={() => handleReject(req)}>Dismiss</Button>
            </>
          )}
          {req.status === "pending" && (
            <Typography variant="caption" color="textSecondary">Provisioning...</Typography>
          )}
        </div>,
      ]}
      keyRowAttr="id"
    />
  );
  /* eslint-enable react/jsx-key */
}

// ─── All Phone Numbers (cross-partner, paginated) ──────────────

function AllPhoneNumbersSection({ classes }) {
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const history = useHistory();

  // Read partner_id from URL on mount
  const urlParams = new URLSearchParams(window.location.search);
  const initialPartnerId = urlParams.get("partner_id") ? parseInt(urlParams.get("partner_id"), 10) : null;

  // Partner filter
  const [partnerId, setPartnerId] = useState(initialPartnerId);
  const [selectedPartner, setSelectedPartner] = useState(null);

  // Sync partner_id to URL
  const updatePartnerId = (option) => {
    const id = option?.value || null;
    setPartnerId(id);
    setSelectedPartner(option || null);
    setPage(1);
    const params = new URLSearchParams(window.location.search);
    if (id) { params.set("partner_id", id); } else { params.delete("partner_id"); }
    history.replace({ search: params.toString() });
  };
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [partnerLoading, setPartnerLoading] = useState(false);

  // Load the selected partner's name when mounted with partner_id from URL
  useEffect(() => {
    if (initialPartnerId && !selectedPartner) {
      api.getPartner({ id: initialPartnerId })
        .then((resp) => {
          const partner = resp?.data || resp;
          if (partner?.id) setSelectedPartner({ label: partner.name, value: partner.id });
        })
        .catch(() => {});
    }
    // Intentionally fires once on mount. Bare disable (not rule-scoped) so
    // it passes both lint-local and CRA's build-time ESLint — the latter
    // doesn't load the react-hooks plugin and errors on unknown rule names.
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setPartnerLoading(true);
    api.getPartners({ page: 1, search: partnerSearch, status: "active", perPage: 15 })
      .then((resp) => setPartnerOptions((resp.data?.items || []).map((p) => ({ label: p.name, value: p.id }))))
      .catch(() => setPartnerOptions([]))
      .finally(() => setPartnerLoading(false));
  }, [partnerSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 25 };
      if (partnerId) params.partner_id = partnerId;

      const resp = await api.getAllPhoneNumbers(params);
      const data = resp?.data || resp;
      setPhoneNumbers(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch {
      toast.error("Failed to load phone numbers");
      setPhoneNumbers([]);
    } finally { setLoading(false); }
  }, [partnerId, page]);

  useEffect(() => { load(); }, [load]);

  // Import dialog
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importPhoneInput, setImportPhoneInput] = useState("");

  const handleImport = async () => {
    if (!partnerId || !importPhoneInput.trim()) return;

    // Normalize: add +1 if missing
    let phone = importPhoneInput.trim();
    if (!phone.startsWith("+")) phone = "+1" + phone.replace(/\D/g, "");

    setImporting(true);
    try {
      const resp = await api.importPhoneNumbersFromTwilio({ id: partnerId, phone_numbers: [phone] });
      const data = resp?.data || resp;
      toast.success(`Imported ${data?.twilio?.imported || 0} from Twilio, ${data?.local?.imported || 0} synced locally`);
      setImportDialogOpen(false);
      setImportPhoneInput("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Import failed");
    } finally { setImporting(false); }
  };

  const handleSyncAll = async () => {
    setImporting(true);
    try {
      const resp = await api.syncPhoneNumbers({ id: partnerId });
      const data = resp?.data || resp;
      toast.success(`Synced: ${data?.imported || 0} new, ${data?.skipped || 0} already synced`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Sync failed");
    } finally { setImporting(false); }
  };

  // Webhook config dialog
  const [editPhone, setEditPhone] = useState(null);
  const [webhookForm, setWebhookForm] = useState({});
  const [saving, setSaving] = useState(false);

  const openWebhookConfig = (phone) => {
    setEditPhone(phone);
    setWebhookForm({
      voiceWebhookUrl: phone.voiceWebhookUrl || "",
      voiceFallbackUrl: phone.voiceFallbackUrl || "",
      voiceStatusCallbackUrl: phone.voiceStatusCallbackUrl || "",
      smsWebhookUrl: phone.smsWebhookUrl || "",
      smsFallbackUrl: phone.smsFallbackUrl || "",
    });
  };

  const handleSaveWebhookConfig = async () => {
    if (!editPhone?.id) {
      toast.error("Phone number record not found");
      return;
    }
    setSaving(true);
    try {
      await api.updateLocalPhoneNumber({
        id: editPhone.partnerId,
        localId: editPhone.id,
        voice_webhook_url: webhookForm.voiceWebhookUrl || null,
        voice_fallback_url: webhookForm.voiceFallbackUrl || null,
        voice_status_callback_url: webhookForm.voiceStatusCallbackUrl || null,
        sms_webhook_url: webhookForm.smsWebhookUrl || null,
        sms_fallback_url: webhookForm.smsFallbackUrl || null,
      });
      toast.success("Webhook config saved");
      setEditPhone(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleSyncTwilioConfig = async (phone) => {
    try {
      await api.syncTwilioConfig({ id: phone.partnerId, localId: phone.id });
      toast.success(`Pulled Twilio config for ${formatPhone(phone.phoneNumber)}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Failed to sync Twilio config");
    }
  };

  // Release dialog
  const [releasePhone, setReleasePhone] = useState(null);
  const [releasing, setReleasing] = useState(false);

  const handleRelease = async () => {
    if (!releasePhone) return;
    setReleasing(true);
    try {
      await api.deleteLocalPhoneNumber({ id: releasePhone.partnerId, localId: releasePhone.id });
      toast.success(`Released ${formatPhone(releasePhone.phoneNumber)}`);
      setReleasePhone(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Failed to release");
    } finally { setReleasing(false); }
  };

  /* eslint-disable react/jsx-key */
  return (
    <>
      <div className={classes.filters}>
        <Autocomplete
          options={partnerLoading ? [{ label: "Loading...", value: null }] : partnerOptions}
          value={selectedPartner}
          getOptionLabel={(opt) => opt.label || ""}
          isOptionEqualToValue={(opt, val) => opt.value === val.value}
          onChange={(_, value) => updatePartnerId(value)}
          onInputChange={(_, value) => setPartnerSearch(value)}
          loading={partnerLoading}
          sx={{ width: 280 }}
          renderInput={(params) => (
            <TextField {...params} label="Filter by Partner" size="small" placeholder="All partners"
              InputProps={{ ...params.InputProps,
                endAdornment: (<>{partnerLoading ? <CircularProgressMui color="inherit" size={18} /> : null}{params.InputProps.endAdornment}</>),
              }}
            />
          )}
        />
        {partnerId && (
          <>
            <Button variant="text" size="small" onClick={() => history.push(`/partners/${partnerId}`)}>View Partner</Button>
            <Button variant="contained" color="primary" size="small" startIcon={<CloudDownloadIcon />} onClick={() => setImportDialogOpen(true)}>
              Import from Twilio
            </Button>
            <Button
              variant="outlined" color="primary" size="small"
              startIcon={importing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
              onClick={handleSyncAll} disabled={importing}
            >
              {importing ? "Syncing..." : "Sync from comm-service"}
            </Button>
          </>
        )}
        <Typography variant="body2" color="textSecondary" style={{ marginLeft: 8 }}>
          {total} number{total !== 1 ? "s" : ""}{partnerId ? "" : " across all partners"}
        </Typography>
        {loading && <CircularProgress size={20} />}
      </div>

      {!loading && phoneNumbers.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          No phone numbers found.{partnerId ? " Try clearing the partner filter." : ""}
        </Typography>
      )}

      {phoneNumbers.length > 0 && (
        <SimpleTable
          rows={phoneNumbers}
          headers={["Phone Number", "Partner", "Source", "Status", "Provisioned", "Released", "Capabilities", "Webhook Config", "Actions"]}
          toCells={(phone) => {
            const webhookLines = [
              phone.voiceWebhookUrl && { label: "Voice", url: phone.voiceWebhookUrl },
              phone.voiceFallbackUrl && { label: "Voice Fallback", url: phone.voiceFallbackUrl },
              phone.voiceStatusCallbackUrl && { label: "Status Callback", url: phone.voiceStatusCallbackUrl },
              phone.smsWebhookUrl && { label: "SMS", url: phone.smsWebhookUrl },
              phone.smsFallbackUrl && { label: "SMS Fallback", url: phone.smsFallbackUrl },
            ].filter(Boolean);

            return [
              phone.phoneNumber ? formatPhone(phone.phoneNumber) : "—",
              <span className={classes.partnerLink} onClick={() => history.push(`/partners/${phone.partnerId}`)}>
                {phone.partnerName || `Partner #${phone.partnerId}`}
              </span>,
              phone.isImported
                ? <Chip size="small" label="Imported" style={{ backgroundColor: "#7c4dff", color: "#fff" }} />
                : <Chip size="small" label="Bought" style={{ backgroundColor: "#00897b", color: "#fff" }} />,
              <StatusChip status={phone.status} classes={classes} />,
              formatDate(phone.createdAt) || "—",
              phone.releasedAt ? formatDate(phone.releasedAt) : "—",
              [phone.canSms && "SMS", phone.canMms && "MMS", phone.canVoice && "Voice"].filter(Boolean).join(", ") || "—",
              webhookLines.length > 0
                ? <Box>
                    {webhookLines.map((w, i) => (
                      <Typography key={i} variant="caption" display="block" style={{ lineHeight: 1.8 }}>
                        <strong>{w.label}:</strong>{" "}
                        <span style={{ color: "#E6007E", wordBreak: "break-all" }}>{w.url}</span>
                      </Typography>
                    ))}
                  </Box>
                : "—",
              <div className={classes.actions}>
                <Button size="small" color="primary" startIcon={<EditIcon />} onClick={() => openWebhookConfig(phone)}>Config</Button>
                <Button size="small" color="primary" startIcon={<RefreshIcon />} onClick={() => handleSyncTwilioConfig(phone)}>Refresh</Button>
                {phone.status !== "released" && (
                  <Button size="small" variant="contained" style={{ backgroundColor: "#d32f2f", color: "#fff" }} onClick={() => setReleasePhone(phone)}>Release</Button>
                )}
              </div>,
            ];
          }}
          keyRowAttr="id"
        />
      )}
      {totalPages > 1 && (
        <Box mt={2} display="flex" justifyContent="center" alignItems="center" gap={2}>
          <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <Typography variant="body2">Page {page} of {totalPages}</Typography>
          <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </Box>
      )}

      <Dialog open={Boolean(releasePhone)} onClose={() => setReleasePhone(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Release Phone Number</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: 8 }}>
            Are you sure you want to release <strong>{releasePhone?.phoneNumber ? formatPhone(releasePhone.phoneNumber) : "this number"}</strong>?
          </Typography>
          {releasePhone?.isImported ? (
            <Typography variant="body2" style={{ color: "#7c4dff", fontWeight: "bold" }}>
              This is an imported number. It will be removed from Boom and webhook URLs will be reset. The number will remain on the partner's Twilio account.
            </Typography>
          ) : (
            <Typography variant="body2" style={{ color: "#d32f2f", fontWeight: "bold" }}>
              This will release the number from Twilio. The number may become available to other accounts after ~30 days. This action cannot be easily undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleasePhone(null)} disabled={releasing}>Cancel</Button>
          <Button variant="contained" color="secondary" onClick={handleRelease} disabled={releasing}>
            {releasing ? "Releasing..." : "Release Number"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Import Phone Number from Twilio</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
            Enter the phone number to import from your Twilio account. The number must already be purchased on Twilio.
          </Typography>
          <TextField
            label="Phone Number"
            value={importPhoneInput}
            onChange={(e) => setImportPhoneInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleImport()}
            fullWidth size="small"
            placeholder="+16169653118 or 6169653118"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained" color="primary"
            onClick={handleImport}
            disabled={importing || !importPhoneInput.trim()}
          >
            {importing ? "Importing..." : "Import"}
          </Button>
        </DialogActions>
      </Dialog>

      {editPhone && (
        <Dialog open={Boolean(editPhone)} onClose={() => setEditPhone(null)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Webhook Configuration — {editPhone.phoneNumber ? formatPhone(editPhone.phoneNumber) : "Phone"}
          </DialogTitle>
          <DialogContent>
            <Box mb={2}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>Voice Configuration</Typography>
              <TextField
                label="A call comes in (Voice URL)"
                value={webhookForm.voiceWebhookUrl}
                onChange={(e) => setWebhookForm((f) => ({ ...f, voiceWebhookUrl: e.target.value }))}
                fullWidth size="small" margin="dense"
                placeholder="https://api.us.elevenlabs.io/twilio/inbound_call"
              />
              <TextField
                label="Primary handler fails (Fallback URL)"
                value={webhookForm.voiceFallbackUrl}
                onChange={(e) => setWebhookForm((f) => ({ ...f, voiceFallbackUrl: e.target.value }))}
                fullWidth size="small" margin="dense"
              />
              <TextField
                label="Call status changes (Status Callback)"
                value={webhookForm.voiceStatusCallbackUrl}
                onChange={(e) => setWebhookForm((f) => ({ ...f, voiceStatusCallbackUrl: e.target.value }))}
                fullWidth size="small" margin="dense"
                placeholder="https://api.us.elevenlabs.io/twilio/status-callback"
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>Messaging Configuration</Typography>
              <TextField
                label="A message comes in (SMS URL)"
                value={webhookForm.smsWebhookUrl}
                onChange={(e) => setWebhookForm((f) => ({ ...f, smsWebhookUrl: e.target.value }))}
                fullWidth size="small" margin="dense"
              />
              <TextField
                label="Primary handler fails (SMS Fallback URL)"
                value={webhookForm.smsFallbackUrl}
                onChange={(e) => setWebhookForm((f) => ({ ...f, smsFallbackUrl: e.target.value }))}
                fullWidth size="small" margin="dense"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditPhone(null)}>Cancel</Button>
            <Button
              variant="contained" color="primary"
              onClick={handleSaveWebhookConfig} disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
  /* eslint-enable react/jsx-key */
}

// ─── Main Page ──────────────────────────────────────────────────

export default function ProvisionedPhoneNumbersPage() {
  const classes = useStyles();
  const [refreshKey, setRefreshKey] = useState(0);

  const handleActionComplete = () => setRefreshKey((k) => k + 1);

  return (
    <AdminContainer variant="list">
      <Box mb={4}>
        <Typography variant="h5" gutterBottom>Provisioned Phone Numbers</Typography>
        <Typography variant="body2" color="textSecondary">
          Manage phone numbers across all partners. Import from Twilio, approve requests, and configure webhook URLs.
        </Typography>
      </Box>

      <Box className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>Requests &amp; Provisioning</Typography>
        <PendingRequestsSection key={`pending-${refreshKey}`} classes={classes} onActionComplete={handleActionComplete} />
      </Box>

      <Box className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>All Phone Numbers</Typography>
        <AllPhoneNumbersSection key={`all-${refreshKey}`} classes={classes} />
      </Box>
    </AdminContainer>
  );
}
